import i18n from "../i18n";
import lodash from "lodash";
import { notification } from "ant-design-vue";
import { scrollToHasError, scrollToQuerySelector } from "./scroll";
import store from "../store";
import moment from "moment";

export const validateError = ({ error }) => {
    if (error?.errorFields?.length > 0) {
        notification["error"]({ message: i18n.global.t("error.form.invalid") });
        scrollToHasError();
    }
};

export const validateNotification = ({ message, querySelector }) => {
    if (message) {
        notification["error"]({ message });
        scrollToQuerySelector(querySelector);
    }
};

export const required = {
    validator: (rule, value) => {
        if (Array.isArray(value)) {
            if (value.filter(d => d.isDelete !== true).length === 0) {
                return Promise.reject(i18n.global.t("error.form.required"));
            }
        } else {
            if (value === undefined || value === null || value?.length === 0 || value?.toString()?.length === 0) {
                return Promise.reject(i18n.global.t("error.form.required"));
            }
        }
        return Promise.resolve();
    },
    required: true,
    trigger: "change"
};

export const email = {
    validator: (rule, value) => {
        if (value) {
            // const regExp = new RegExp("^.+@.+\\..+$", "i");
            const regExp = new RegExp("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z0-9]{2,6}$", "i");
            if (!regExp.test(value)) {
                return Promise.reject(i18n.global.t("error.form.email"));
            }
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const isAlphabetOrNumber = {
    validator: (rule, value) => {
        if (value) {
            const regExp = new RegExp("^[A-Za-z0-9_.]+$", "i");
            if (!regExp.test(value)) {
                return Promise.reject(i18n.global.t("error.form.format"));
            }
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const isStringNumber = {
    validator: (rule, value) => {
        if (value) {
            const regExp = new RegExp("^[0-9_.]+$", "i");
            if (!regExp.test(value)) {
                return Promise.reject(i18n.global.t("error.form.formatNumber"));
            }
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const thPid = {
    validator: (rule, value) => {
        if (value) {
            if (value.length !== 13) {
                return Promise.reject(i18n.global.t("error.form.pid"));
            }
            let sum = 0;
            for (let i = 0; i < 12; i++) {
                sum += parseFloat(value.charAt(i)) * (13 - i);
            }
            if ((11 - (sum % 11)) % 10 !== parseFloat(value.charAt(12))) {
                return Promise.reject(i18n.global.t("error.form.pid"));
            }
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const equalsLength = equalsLength => {
    return {
        validator: (rule, value) => {
            if (value) {
                if (value.length !== equalsLength) {
                    return Promise.reject(i18n.global.t("error.form.equalsLength", { equals: equalsLength }));
                }
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const noHyphenValidator = () => {
    return {
        validator: (rule, value) => {
            if (value && value.includes('-')) {
                return Promise.reject(
                    i18n.global.t("error.form.noHyphenValidator")
                );
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const maxLength = maxLength => {
    return {
        validator: (rule, value) => {
            if (value) {
                if (value.length > maxLength) {
                    if (Array.isArray(value)) {
                        return Promise.reject(i18n.global.t("error.form.maxLengthArr", { max: maxLength }));
                    } else {
                        return Promise.reject(i18n.global.t("error.form.maxLength", { max: maxLength }));
                    }
                }
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const minLength = minLength => {
    return {
        validator: (rule, value) => {
            if (value) {
                if (value.length < minLength) {
                    if (Array.isArray(value)) {
                        return Promise.reject(i18n.global.t("error.form.minLengthArr", { min: minLength }));
                    } else {
                        return Promise.reject(i18n.global.t("error.form.minLength", { min: minLength }));
                    }
                }
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const keyDup = key => {
    return {
        validator: (rule, value) => {
            const obj = lodash.groupBy(value, key);
            const keyDup =
                Object.keys(obj)
                    .map(key => obj[key].length)
                    .filter(length => length > 1).length > 0;
            if (keyDup) {
                return Promise.reject(i18n.global.t("error.form.keyDup"));
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const same = (form, key, keyLabel, sameLabel) => {
    return {
        validator: (rule, value) => {
            const same = form[key] === value;
            if (!same) {
                return Promise.reject(i18n.global.t("error.form.same", { key: keyLabel, same: sameLabel }));
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const sameWhen = ({ when, keyLabel, sameLabel }) => {
    return {
        validator: (rule, value) => {
            const same = when?.({ rule, value });
            if (!same) {
                return Promise.reject(i18n.global.t("error.form.same", { key: keyLabel, same: sameLabel }));
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const greaterThan = num => {
    return {
        validator: (rule, value) => {
            if (value || value === 0) {
                if (parseFloat(value) <= num) {
                    return Promise.reject(i18n.global.t("error.form.greaterThan", { num: num }));
                }
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const greaterThanOrEqualTo = num => {
    return {
        validator: (rule, value) => {
            if (value || value === 0) {
                if (parseFloat(value) < num) {
                    return Promise.reject(i18n.global.t("error.form.greaterThanOrEqualTo", { num: num }));
                }
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const lessThanOrEqualTo = num => {
    return {
        validator: (rule, value) => {
            if (value || value === 0) {
                if (parseFloat(value) > num) {
                    return Promise.reject(i18n.global.t("error.form.lessThanOrEqualTo", { num: num }));
                }
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const json = {
    validator: (rule, value) => {
        if (value) {
            try {
                JSON.parse(value);
            } catch (error) {
                return Promise.reject(i18n.global.t("error.form.json"));
            }
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const requiredWhen = ({ when, requiredValidator }) => {
    return {
        validator: (rule, value) => {
            if (when?.({ rule, value })) {
                if (requiredValidator) {
                    const valid = requiredValidator({ rule, value });
                    if (valid?.then) {
                        return valid;
                    }
                    return valid ? Promise.resolve() : Promise.reject(i18n.global.t("error.form.required"));
                }
                return required.validator(rule, value);
            }
            return Promise.resolve();
        },
        trigger: "change",
        when
    };
};

export const hasUsername = {
    validator: (rule, value, isCheckDraft, idIgnore) => {
        if (value) {
            return store.dispatch("resource/v1/security/user/hasUsername", { username: value.trim(), isCheckDraft, idIgnore }).then(has => {
                if (has) {
                    return Promise.reject(i18n.global.t("error.form.hasUsername"));
                }
                return Promise.resolve();
            });
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const hasIdCard = ({ cardKey, idIgnore }) => {
    return {
        validator: (rule, value) => {
            if (value) {
                return store
                    .dispatch("resource/v1/security/user/hasIdCard", { cardKey: cardKey?.(), idCard: value, idIgnore: idIgnore?.() })
                    .then(has => {
                        if (has) {
                            return Promise.reject(i18n.global.t("error.form.hasIdCard"));
                        }
                        return Promise.resolve();
                    });
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const checkUsedInvoices = ({ dataFunc }) => {
    return {
        validator: (rule, value) => {
            const data = dataFunc();
            if (["3", "4"].includes(data.requestTypeKey)) {
                return Promise.resolve();
            }
            if (data?.createDate && moment("2024-06-17").isAfter(data.createDate)) {
                return Promise.resolve();
            }
            if (value) {
                return store
                    .dispatch("resource/v1/transaction/informProduct/searchUsedInvoices", { data })
                    .then(result => {
                        if (result > 0 || result?.length > 0) {
                            return Promise.reject(i18n.global.t("error.form.invoiceCodeUsed"));
                        }
                        return Promise.resolve();
                    });
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const hasIdCardActive = ({ cardKey, idIgnore }) => {
    return {
        validator: (rule, value) => {
            if (value) {
                return store
                    .dispatch("resource/v1/security/user/hasIdCardActive", { cardKey: cardKey?.(), idCard: value, idIgnore: idIgnore?.() })
                    .then(has => {
                        if (has) {
                            return Promise.reject(i18n.global.t("error.form.hasIdCard"));
                        }
                        return Promise.resolve();
                    });
            }
            return Promise.resolve();
        },
        trigger: "blur"
    };
};

export const geoojson = {
    validator: (rule, value) => {
        if (value) {
            const gjv = require("geojson-validation");
            if (!gjv.valid(value)) {
                return Promise.reject(i18n.global.t("error.form.geojson"));
            }
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const geojsonLatlng = {
    validator: (rule, value) => {
        if (value) {
            const valueValidate = lodash.cloneDeep(value);
            valueValidate.coordinates = valueValidate.coordinates.map(d => parseFloat(d)).map(d => (isNaN(d) ? null : d));
            const gjv = require("geojson-validation");
            if (!gjv.valid(valueValidate)) {
                return Promise.reject(i18n.global.t("error.form.geojsonLatlng"));
            }
        }
        return Promise.resolve();
    },
    trigger: "blur"
};

export const clearRules = ({ useForm, key }) => {
    const { rulesRef, validateInfos } = useForm;
    Object.keys(validateInfos)
        .filter(k => k === key)
        .forEach(k => delete validateInfos[k]);
    Object.keys(rulesRef)
        .filter(k => k === key)
        .forEach(k => delete rulesRef[k]);
};

export const clearRulesStartsWith = ({ useForm, key }) => {
    const { rulesRef, validateInfos } = useForm;
    let keys = key;
    if (!Array.isArray(keys)) {
        keys = [keys];
    }
    keys.forEach(key => {
        Object.keys(validateInfos)
            .filter(k => k.startsWith(key))
            .forEach(k => delete validateInfos[k]);
        Object.keys(rulesRef)
            .filter(k => k.startsWith(key))
            .forEach(k => delete rulesRef[k]);
    });
};

export const addRules = ({ useForm, modelRef, key, defaultValue = null, rules, promise = true }) => {
    const { validateInfos, rulesRef } = useForm;
    if (promise) {
        Promise.resolve()
            .then(() => {
                if (lodash.get(modelRef, `${key}`) === undefined || lodash.get(modelRef, `${key}`) === null) {
                    lodash.set(modelRef, `${key}`, defaultValue);
                }
            })
            .then(() => {
                validateInfos[`${key}`] = { autoLink: false, required: rules.filter(d => d.required).length > 0 };
                rulesRef[`${key}`] = rules;
            });
    } else {
        if (lodash.get(modelRef, `${key}`) === undefined || lodash.get(modelRef, `${key}`) === null) {
            lodash.set(modelRef, `${key}`, defaultValue);
        }
        validateInfos[`${key}`] = { autoLink: false, required: rules.filter(d => d.required).length > 0 };
        rulesRef[`${key}`] = rules;
    }
};
